/* eslint-disable import/no-cycle */
import React, { useRef } from "react"
import { ThemeProvider, useTheme } from "styled-components"
import striptags from "striptags"

import useDebouncedFiltering from "hooks/useDebouncedFiltering"
import { useComponentizedPage } from "store/contexts/componentized-page"
import componentThemeExtension from "utils/componentThemeExtension"
import cloudinaryImagePerDevice from "utils/cloudinaryImagePerDevice"

import TextAndMediaVertical from "components/text-and-media"
import TextAndImage from "components/text-and-media/text-and-image"
import ScrollableGrid from "components/scrollable-grid"
import ContentfulButton from "./button"
import ContentfulImage from "./image"
import ContentfulPerson from "./person"
import ContentfulVideo from "./video"
import ContentfulSignupForm from "./sign-up-form"
import ContentfulCarousel from "./carousel"

const ContentfulTextWithMedia = ({
  className,
  component,
  onTop,
  elRef,
  inGrid,
  trackingId,
  isPromotionalPlacement,
  componentOverrideTheme,
}) => {
  const {
    media,
    mediaPosition,
    backgroundColor,
    backgroundImage,
    preHeadingStyle,
    preHeadingText,
    preHeadingLogo,
    heading,
    headingStyle,
    subheadingTM,
    bodyText,
    navigationAnchor,
    button,
    secondButton,
    signupForm,
    textAlignment,
    id,
    hideOnPageLoad,
    theme,
    featureHighlights,
  } = component

  const mediaType = media?.internal.type || ""

  const { isHomePage } = useComponentizedPage()

  // check if media is a carousel containing a grid of articles or a grid of products
  let hasArticleGrid = false
  let hasProductGrid = false
  let filteringProps = {}

  if (isHomePage && mediaType === "ContentfulCarousel") {
    const carouselItems = media.items
    if (carouselItems && carouselItems.length) {
      const carouselItemType = carouselItems[0].internal.type
      if (carouselItemType === "ContentfulGridLayout") {
        const gridItems = carouselItems[0].items
        if (gridItems && gridItems.length) {
          const gridItemType = gridItems[0].internal.type
          if (gridItemType === "ContentfulCard") {
            if (gridItems[0].itemType === "Article") {
              hasArticleGrid = true
              filteringProps = { items: gridItems }
            } else if (gridItems[0].itemType === "Product") {
              hasProductGrid = true
              filteringProps = {
                items: gridItems,
                filterBy: (
                  value,
                  {
                    shopifyProductVariant: {
                      product: { title, description },
                    },
                  },
                ) => title.includes(value) || description.includes(value),
              }
            }
          }
        }
      }
    }
  }

  const { searchQuery, setSearchQuery, filteredItems } = useDebouncedFiltering(filteringProps)

  const preheadingProps = {
    style: preHeadingStyle,
    logo: preHeadingLogo ? preHeadingLogo[0].secure_url : null,
    aspectLogo: preHeadingLogo ? preHeadingLogo[0].height / preHeadingLogo[0].width : 0,
    logoLeft: true,
    useLineOnMobile: hasProductGrid,
  }

  if (preHeadingText) {
    const text = preHeadingText.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")
    preheadingProps[preHeadingStyle === "mbg logo font" ? "monolineText" : "text"] = text
  }

  const headingText = heading && heading.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")
  const headingProps = {
    ...(headingText
      ? headingStyle === "Serif"
        ? { serifText: headingText }
        : { text: headingText }
      : {}),
  }

  // If text is longer than 480 characters, set a minimum height for the image,
  // because that implies that image will be tall and narrow.
  // Also, the number of characters is a good approximation for the height of the image.
  const text = bodyText?.childMarkdownRemark?.html
    ? striptags(bodyText.childMarkdownRemark.html)
    : ""

  let isVerticalImage = false
  if (mediaType === "ContentfulImage") {
    const { width, height } = media.image[0]
    isVerticalImage = width < height
  }

  let mediaElement
  switch (mediaType) {
    case "ContentfulImage":
      mediaElement = (
        <ContentfulImage
          component={{
            ...media,
            useOriginalAspectRatio: inGrid,
            ...(text.length > 480 && { minHeight: Math.round(text.length * 0.9) }),
          }}
          onTop={onTop}
          nested
          {...(text.length > 480 && { crop: true })}
        />
      )
      break

    case "ContentfulVideo":
      mediaElement = <ContentfulVideo component={media} nested />
      break

    case "ContentfulCarousel":
      mediaElement = hasArticleGrid ? (
        <ScrollableGrid
          items={filteredItems}
          pageType="Landing"
          tracking={media.tracking}
          trackingId={media.trackingId}
        />
      ) : hasProductGrid ? (
        <ScrollableGrid
          items={filteredItems}
          pageType="Landing"
          tracking={media.tracking}
          trackingId={media.trackingId}
        />
      ) : (
        <ContentfulCarousel component={media} onTop={onTop} nested />
      )
      break

    case "ContentfulPerson":
      mediaElement = <ContentfulPerson component={media} hideIdentity />
      break

    default:
  }

  if (inGrid) {
    return (
      <ThemeProvider
        theme={componentThemeExtension(
          backgroundColor,
          false,
          onTop,
          componentOverrideTheme || theme,
        )}
      >
        <TextAndMediaVertical
          elRef={elRef}
          dataId={id}
          dataExpId={trackingId}
          className={className}
          heading={headingProps.text}
          body={bodyText && bodyText.childMarkdownRemark.html}
          mediaSlotA={
            (mediaPosition === "Media Above Text" && mediaElement) ||
            (!mediaPosition && mediaElement)
          }
          mediaSlotB={mediaPosition === "Media Below Headline" && mediaElement}
          mediaSlotC={mediaPosition === "Media Below Text" && mediaElement}
          buttonElement={button && <ContentfulButton component={button} />}
          secondButtonElement={secondButton && <ContentfulButton component={secondButton} />}
          centered={textAlignment === "Centered"}
        />
      </ThemeProvider>
    )
  }

  const backgroundImages = backgroundImage ? cloudinaryImagePerDevice(backgroundImage) : null

  const signupRef = useRef(null)

  return (
    <ThemeProvider
      theme={componentThemeExtension(
        backgroundColor,
        false,
        onTop,
        componentOverrideTheme || theme,
      )}
    >
      <TextAndImage
        dataId={id}
        dataExpId={trackingId}
        className={className}
        id={navigationAnchor && navigationAnchor.anchorId}
        elRef={elRef}
        hideOnPageLoad={hideOnPageLoad}
        reverseOrder={mediaPosition === "Left"}
        mediaType={mediaType}
        mediaElement={mediaElement}
        preheading={preheadingProps}
        heading={headingProps}
        subheading={subheadingTM}
        body={bodyText && bodyText.childMarkdownRemark.html}
        buttonElement={button && <ContentfulButton component={button} />}
        secondButtonElement={secondButton && <ContentfulButton component={secondButton} />}
        signupForm={
          signupForm && (
            <ContentfulSignupForm
              elRef={signupRef}
              component={signupForm}
              nested
              hasMedia={!!mediaElement}
              isPromotionalPlacement={isPromotionalPlacement}
            />
          )
        }
        variantLeft={textAlignment}
        backgroundImages={backgroundImages}
        verticalAlign="top"
        applyTopBottomPadding={isVerticalImage}
        applyMediaPadding={media?.internal?.type === "ContentfulPerson"}
        isShop={hasProductGrid}
        featureHighlights={featureHighlights}
        // topBar={
        //   hasProductGrid && (
        //     <HeaderWithSearch
        //       searchQuery={searchQuery}
        //       setSearchQuery={setSearchQuery}
        //       title="shop"
        //       label="Search products"
        //     />
        //   )
        // }
      />
    </ThemeProvider>
  )
}

export default ContentfulTextWithMedia
